/**
 * Subscription forms — where consent is collected.
 *
 * Everything else in this product proves consent. This is the one screen where
 * it is obtained, and the difference matters: a contact created through the API
 * or a CSV is the organization asserting on somebody's behalf, while a confirmed
 * form submission is the address owner asserting for themselves.
 *
 * The screen leads with the endpoint URL and a paste-ready snippet, because the
 * form itself is not built here — the customer writes their own HTML and posts
 * to us. What this owns is the pipeline behind it, and the two counts are how
 * you tell that pipeline is working: pending far above confirmed means the
 * confirmation mail is not arriving, which is the failure mode single opt-in
 * does not have and therefore the one nobody thinks to look for.
 */

import { useState, type FormEvent } from "react";

import { api, ApiError } from "../api/client";
import { useAsync } from "../hooks";
import { SkeletonRows } from "../components/Loading";
import { Icon } from "../components/Sprites";
import { useSession } from "../session";
import type { Form } from "../api/types";

export function Forms() {
  const { orgId, can } = useSession();
  const manageable = can("forms:manage");

  const { data, error, loading, reload } = useAsync<Form[]>(
    (signal) => api.get<Form[]>("/v1/forms", { signal }),
    [orgId],
  );

  const forms = data ?? [];

  return (
    <>
      <div className="topbar">
        <h1 className="oc-headline-lg">Forms</h1>
        <div className="row">
          {manageable && <CreateForm onCreated={reload} />}
          <button type="button" className="oc-btn oc-btn--ghost oc-btn--sm" onClick={reload}>
            <Icon name="refresh" size={12} />
            Refresh
          </button>
        </div>
      </div>

      <div className="content">
        {error && (
          <div className="oc-panel oc-panel--critical" role="alert">
            <div className="oc-panel__body">
              <span className="oc-label">Forms are unavailable</span>
              <p className="oc-body-sm" style={{ marginBottom: 0 }}>
                {error.message}
              </p>
            </div>
          </div>
        )}

        {loading && forms.length === 0 && (
          <div className="oc-panel">
            <div className="oc-panel__body">
              <SkeletonRows columns={3} />
            </div>
          </div>
        )}

        {!loading && !error && forms.length === 0 && (
          <div className="oc-panel">
            <div className="oc-panel__body">
              <div className="oc-empty">
                <div className="oc-empty__title">No forms yet</div>
                <div className="oc-empty__hint">
                  A form gives you a public endpoint to post an email address to. Everyone
                  who submits gets a confirmation email, and only a confirmed address is
                  ever subscribed — which is what makes the consent defensible if anybody
                  later asks you to prove it.
                </div>
              </div>
            </div>
          </div>
        )}

        {forms.map((form) => (
          <FormCard key={form.id} form={form} manageable={manageable} onChanged={reload} />
        ))}

        {forms.length > 0 && (
          <p className="oc-body-sm oc-text-secondary" style={{ margin: 0 }}>
            Confirmation emails are sent from the address on each form, which must be on a
            verified domain — otherwise a submission is accepted and its confirmation never
            arrives, so the signup silently never completes.
          </p>
        )}
      </div>
    </>
  );
}

function FormCard({
  form,
  manageable,
  onChanged,
}: {
  form: Form;
  manageable: boolean;
  onChanged: () => void;
}) {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const pending = form.pending_count ?? 0;
  const confirmed = form.confirmed_count ?? 0;

  // Only worth flagging once there is enough to compare. Two pending and no
  // confirmations on a form somebody made a minute ago is not a problem.
  const stalled = form.double_opt_in && pending >= 5 && pending > confirmed * 2;

  async function toggle() {
    setBusy(true);
    setError(null);
    try {
      await api.patch(`/v1/forms/${form.id}`, { enabled: !form.enabled });
      onChanged();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "Could not update this form.");
    } finally {
      setBusy(false);
    }
  }

  async function remove() {
    if (!window.confirm(
      `Delete "${form.name}"? Its endpoint stops accepting submissions immediately. ` +
      `Contacts it already created keep their subscriptions.`
    )) {
      return;
    }
    setBusy(true);
    setError(null);
    try {
      await api.delete(`/v1/forms/${form.id}`);
      onChanged();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "Could not delete this form.");
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="oc-panel">
      <div className="oc-panel__header">
        <span className="oc-label">{form.name}</span>
        <span className="row" style={{ gap: "var(--oc-space-2)" }}>
          <span className={`oc-status form-state${form.enabled ? "" : " form-state--off"}`}>
            {form.enabled ? "Accepting" : "Paused"}
          </span>
          {manageable && (
            <>
              <button
                type="button"
                className="oc-btn oc-btn--ghost oc-btn--sm"
                onClick={toggle}
                disabled={busy}
              >
                {form.enabled ? "Pause" : "Resume"}
              </button>
              <button
                type="button"
                className="oc-btn oc-btn--ghost oc-btn--sm"
                onClick={remove}
                disabled={busy}
              >
                Delete
              </button>
            </>
          )}
        </span>
      </div>

      <div className="oc-panel__body stack">
        {error && <p className="field-error">{error}</p>}

        <div className="metrics">
          <div className="oc-metric">
            <span className="oc-metric__value">{confirmed.toLocaleString()}</span>
            <span className="oc-label">Confirmed</span>
          </div>
          <div className="oc-metric">
            <span className={`oc-metric__value${stalled ? " oc-metric__value--negative" : ""}`}>
              {pending.toLocaleString()}
            </span>
            <span className="oc-label">Awaiting confirmation</span>
          </div>
          <div className="oc-metric">
            <span className="oc-metric__value">{form.double_opt_in ? "Double" : "Single"}</span>
            <span className="oc-label">Opt-in</span>
          </div>
        </div>

        {stalled && (
          <p className="oc-body-sm" style={{ margin: 0, color: "var(--oc-warning)" }}>
            Most submissions here are not being confirmed. The usual cause is that the
            confirmation email is not arriving — check that <strong>{form.from_addr}</strong>{" "}
            is on a verified domain.
          </p>
        )}

        {!form.double_opt_in && (
          <p className="oc-body-sm oc-text-secondary" style={{ margin: 0 }}>
            Single opt-in: a submission subscribes immediately, so anybody can subscribe
            anybody — the address is never proved to belong to the person typing it.
            Reasonable behind a signed-in account page, risky on a public site.
          </p>
        )}

        <div className="stack" style={{ gap: "var(--oc-space-2)" }}>
          <span className="oc-label">Endpoint</span>
          <Copyable value={form.submit_url} />
        </div>

        <AllowedSites form={form} manageable={manageable} onChanged={onChanged} />

        <details>
          <summary className="oc-body-sm">
            <Icon name="chevron" className="caret oc-text-secondary" /> Paste-ready HTML
          </summary>
          <pre className="oc-code form-snippet">{snippetFor(form)}</pre>
          <p className="oc-body-sm oc-text-secondary" style={{ margin: 0 }}>
            A plain form like this, or JSON from your own code. The endpoint needs no
            credential, which is what lets it live on a public page. Keep the hidden
            field: a person never sees it, and a post that fills it in is dropped as a
            bot's.
            {(form.allowed_origins?.length ?? 0) > 0 &&
              " With allowed sites set, a post has to come from a browser on one of them — code on a server sends no Origin header and is refused."}
          </p>
        </details>
      </div>
    </div>
  );
}

/**
 * The sites a form accepts posts from, editable in place.
 *
 * Editable here rather than only at creation because a form that already lives
 * on a page cannot be recreated to gain a list: a new form has a new public id,
 * and every copy of the HTML pasted into the customer's site would stop working.
 */
function AllowedSites({
  form,
  manageable,
  onChanged,
}: {
  form: Form;
  manageable: boolean;
  onChanged: () => void;
}) {
  const [editing, setEditing] = useState(false);
  const [value, setValue] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const sites = form.allowed_origins ?? [];

  async function save(event: FormEvent) {
    event.preventDefault();
    setBusy(true);
    setError(null);
    try {
      // Empty clears the list, which is how a form goes back to any site.
      await api.patch(`/v1/forms/${form.id}`, {
        allowed_origins: value.split(/[\s,]+/).filter(Boolean),
      });
      setEditing(false);
      onChanged();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "Could not update the allowed sites.");
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="stack" style={{ gap: "var(--oc-space-2)" }}>
      <span className="oc-label">Accepts posts from</span>
      {editing ? (
        <form className="row" onSubmit={save} style={{ gap: "var(--oc-space-2)" }}>
          <input
            className="oc-input oc-input--sm"
            value={value}
            onChange={(e) => setValue(e.target.value)}
            placeholder="https://www.example.com"
            aria-label="Sites this form may be posted from, separated by commas. Empty accepts any site."
            aria-invalid={error ? true : undefined}
          />
          <button type="submit" className="oc-btn oc-btn--primary oc-btn--sm" disabled={busy}>
            Save
          </button>
          <button
            type="button"
            className="oc-btn oc-btn--ghost oc-btn--sm"
            onClick={() => setEditing(false)}
          >
            Cancel
          </button>
          {error && <span className="field-error">{error}</span>}
        </form>
      ) : (
        <span className="row" style={{ gap: "var(--oc-space-2)" }}>
          <span className="oc-body-sm">
            {sites.length > 0
              ? sites.join(", ")
              : manageable
                ? "Any site. List the sites this form lives on to refuse posts from anywhere else."
                : "Any site."}
          </span>
          {manageable && (
            <button
              type="button"
              className="oc-btn oc-btn--ghost oc-btn--sm"
              onClick={() => {
                setValue(sites.join(", "));
                setError(null);
                setEditing(true);
              }}
            >
              Edit
            </button>
          )}
        </span>
      )}
    </div>
  );
}

/** A value with a copy button, matching how API keys and DNS records are shown. */
function Copyable({ value }: { value: string }) {
  const [copied, setCopied] = useState(false);

  return (
    <div className="oc-copy">
      <span className="oc-copy__value oc-mono-sm">{value}</span>
      <button
        type="button"
        className="oc-btn oc-btn--ghost oc-btn--sm oc-copy__btn"
        onClick={() => {
          void navigator.clipboard.writeText(value);
          setCopied(true);
          window.setTimeout(() => setCopied(false), 1500);
        }}
      >
        <Icon name={copied ? "check" : "copy"} size={12} />
        {copied ? "Copied" : "Copy"}
      </button>
    </div>
  );
}

function snippetFor(form: Form): string {
  return [
    `<form method="post" action="${form.submit_url}">`,
    `  <label for="email">Email</label>`,
    `  <input id="email" type="email" name="email" required>`,
    `  <input type="text" name="name" placeholder="Name (optional)">`,
    `  <!-- Leave empty: a bot fills in every field, and a post with this one set is dropped. -->`,
    `  <input type="text" name="oc_hp" tabindex="-1" autocomplete="off" aria-hidden="true"`,
    `         style="position:absolute;left:-9999px">`,
    `  <button type="submit">Subscribe</button>`,
    `</form>`,
  ].join("\n");
}

function CreateForm({ onCreated }: { onCreated: () => void }) {
  const [open, setOpen] = useState(false);
  const [name, setName] = useState("");
  const [from, setFrom] = useState("");
  const [origins, setOrigins] = useState("");
  const [doubleOptIn, setDoubleOptIn] = useState(true);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function submit(event: FormEvent) {
    event.preventDefault();
    setBusy(true);
    setError(null);
    try {
      await api.post("/v1/forms", {
        name: name.trim(),
        from_addr: from.trim(),
        double_opt_in: doubleOptIn,
        // Commas or spaces between sites. The server reduces each to an origin
        // and refuses anything that is not one.
        allowed_origins: origins.split(/[\s,]+/).filter(Boolean),
      });
      setOpen(false);
      setName("");
      setFrom("");
      setOrigins("");
      setDoubleOptIn(true);
      onCreated();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "Could not create the form.");
    } finally {
      setBusy(false);
    }
  }

  if (!open) {
    return (
      <button type="button" className="oc-btn oc-btn--primary oc-btn--sm" onClick={() => setOpen(true)}>
        <Icon name="plus" size={12} />
        New form
      </button>
    );
  }

  return (
    <form className="row" onSubmit={submit} style={{ gap: "var(--oc-space-2)" }}>
      <input
        className="oc-input oc-input--sm"
        placeholder="Name"
        value={name}
        onChange={(e) => setName(e.target.value)}
        aria-label="Form name"
        aria-invalid={error ? true : undefined}
        required
      />
      <input
        className="oc-input oc-input--sm"
        placeholder="Confirmation sender"
        value={from}
        onChange={(e) => setFrom(e.target.value)}
        aria-label="Sender address for the confirmation email"
        required
      />
      <input
        className="oc-input oc-input--sm"
        placeholder="Allowed sites (optional)"
        value={origins}
        onChange={(e) => setOrigins(e.target.value)}
        aria-label="Sites this form may be posted from, separated by commas. Empty accepts any site."
      />
      <label className="oc-body-sm row" style={{ gap: "var(--oc-space-1)" }}>
        <input
          type="checkbox"
          checked={doubleOptIn}
          onChange={(e) => setDoubleOptIn(e.target.checked)}
        />
        Double opt-in
      </label>
      <button type="submit" className="oc-btn oc-btn--primary oc-btn--sm" disabled={busy}>
        Create
      </button>
      <button
        type="button"
        className="oc-btn oc-btn--ghost oc-btn--sm"
        onClick={() => setOpen(false)}
      >
        Cancel
      </button>
      {error && <span className="field-error">{error}</span>}
    </form>
  );
}
